// frontend/src/components/Dashboard/MotivosBajaChart.js
// Gráfica de motivos de baja del boletín (panel admin > Notificaciones >
// Boletín). Lee GET /api/boletines/bajas/resumen.

import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js';
import { UserMinus } from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

// Misma paleta validada que TraficoCard (claro / oscuro)
const COLORES = {
  claro: { barra: '#0B8A78', texto: '#8A94A6', etiqueta: '#172B35', grid: '#E6EBEE' },
  oscuro: { barra: '#2A9E8C', texto: '#7C928C', etiqueta: '#EAF2F0', grid: '#324542' }
};

const MotivosBajaChart = ({ modoOscuro, recargar }) => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelado = false;
    fetch('/api/boletines/bajas/resumen', { credentials: 'include' })
      .then(async r => {
        const data = await r.json().catch(() => {
          throw new Error(`No se pudieron cargar los motivos de baja (el servidor respondió ${r.status} sin datos válidos).`);
        });
        if (!r.ok) throw new Error(data.error || 'No se pudieron cargar los motivos de baja');
        if (!cancelado) setDatos(data);
      })
      .catch(err => { if (!cancelado) setError(err.message); });
    return () => { cancelado = true; };
  }, [recargar]);

  const c = modoOscuro ? COLORES.oscuro : COLORES.claro;

  if (error) return <p className="text-muted">{error}</p>;
  if (!datos) return <p className="text-muted">Cargando motivos de baja...</p>;

  // Ordenados de mayor a menor: la barra más larga arriba
  const motivos = [...datos.motivos].sort((a, b) => b.total - a.total);
  const totalConMotivo = datos.con_motivo;

  const chartData = {
    labels: motivos.map(m => m.texto),
    datasets: [{
      label: 'Bajas',
      data: motivos.map(m => m.total),
      backgroundColor: c.barra,
      borderRadius: 4,
      borderSkipped: 'start',
      maxBarThickness: 22
    }]
  };

  const chartOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const pct = totalConMotivo ? Math.round((ctx.raw / totalConMotivo) * 100) : 0;
            return ` ${ctx.raw} baja(s) · ${pct}% de las que indicaron motivo`;
          }
        }
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        grid: { color: c.grid },
        border: { display: false },
        ticks: { color: c.texto, precision: 0 }
      },
      y: {
        grid: { display: false },
        border: { color: c.grid },
        ticks: { color: c.etiqueta, font: { size: 12 } }
      }
    }
  };

  return (
    <div className="motivos-baja">
      <div className="motivos-baja-header">
        <h3><UserMinus size={16} /> Motivos de baja</h3>
        <span className="text-muted">
          {datos.total_bajas} baja(s) en total · {totalConMotivo} indicaron un motivo
        </span>
      </div>

      {datos.total_bajas === 0 ? (
        <p className="text-muted">Nadie se ha dado de baja del boletín todavía.</p>
      ) : (
        <>
          <div className="motivos-baja-grafica">
            <Bar data={chartData} options={chartOptions} aria-label="Número de bajas del boletín por motivo" />
          </div>

          {datos.comentarios_recientes.length > 0 && (
            <div className="motivos-baja-comentarios">
              <h4>Comentarios recientes</h4>
              <ul>
                {datos.comentarios_recientes.map((cm, i) => (
                  <li key={i}>
                    <p>“{cm.comentario}”</p>
                    <small>
                      {cm.empresa_nombre || 'Empresa'}{cm.motivo_texto ? ` · ${cm.motivo_texto}` : ''} · {new Date(cm.created_at).toLocaleDateString('es-MX')}
                    </small>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MotivosBajaChart;
